import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import styled from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { Theme } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/theme'
import { ConsentBannerRecipients } from '@lyrahealth-inc/shared-app-logic/src/features/consent/messages'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { ConsentBannerStatus } from '@lyrahealth-inc/shared-app-logic/src/models/consent/Consent'

import providerSessionAlertSound from '../../assets/sounds/providerSessionAlert.wav'
import { AvatarDetails } from '../../atoms/icons/Avatar'
import {
  AddToastFunction,
  DisplayMode,
  JoinSessionParams,
  RecordSessionProps,
  RemoteParticipantStatus,
} from '../../hooks/useZoom/types'
import { useZoom } from '../../hooks/useZoom/useZoom'
import { ZoomFullScreenView } from '../../molecules/zoomFullScreenView/ZoomFullScreenView'
import { ZoomPermissionsModal } from '../../molecules/zoomPermissionsModal/ZoomPermissionsModal'
import { ZoomVideoBanner } from '../../molecules/zoomVideoBanner/ZoomVideoBanner'
import { ThemeProvider } from '../../utils/themes/ThemeProvider'
import { InformedConsentSessionBanner } from '../informedConsentSessionBanner/InformedConsentSessionBanner'
import { RecordingConsentBanner } from '../recordingConsentBanner/RecordingConsentBanner'

export type InformedConsentBannerProps = {
  status: ConsentBannerStatus
  recipients: ConsentBannerRecipients
  onDismiss: () => void
}

export type ZoomVideoSessionDesktopProps = {
  getSessionParams: () => Promise<JoinSessionParams>
  appointment: Appointment
  participantName: string
  participantFirstName?: string
  avatarDetails?: AvatarDetails
  participantTitle?: string
  onSessionOpened?: () => void
  onSessionStarted?: () => void
  onSessionEnded?: () => void
  onSessionClosed?: () => void
  messenger?: React.ReactElement
  onOpenMessagesPressed?: () => void
  unreadMessageCount?: number
  addToast?: AddToastFunction
  onDisableRecordingPressed?: () => void
  showRecordingConsentBanner?: boolean
  recordSessionProps?: RecordSessionProps
  hideSessionNumber?: boolean
  hideRecordingWarning?: boolean
  hideNotRecordingBadge?: boolean
  informedConsentBanner?: InformedConsentBannerProps
  onInformedConsentBannerHeightChange?: (height: number) => void
}

const Container = styled.View<{ fixed: boolean; fullscreen: boolean }>(({ fixed, fullscreen }) => ({
  position: fixed ? 'fixed' : undefined,
  top: 0,
  bottom: fullscreen ? 0 : undefined,
  right: 0,
  left: 0,
  zIndex: 100,
}))

export const ZoomVideoSessionDesktop: FunctionComponent<ZoomVideoSessionDesktopProps> = ({
  getSessionParams,
  appointment,
  participantName,
  participantFirstName,
  onSessionOpened,
  onSessionStarted,
  onSessionEnded,
  onSessionClosed,
  avatarDetails,
  participantTitle,
  messenger,
  onOpenMessagesPressed,
  unreadMessageCount,
  addToast,
  onDisableRecordingPressed,
  showRecordingConsentBanner,
  recordSessionProps,
  hideSessionNumber,
  hideRecordingWarning = false,
  hideNotRecordingBadge,
  informedConsentBanner,
  onInformedConsentBannerHeightChange,
}) => {
  const {
    joinSession,
    displayMode,
    setSessionEndedCallback,
    setSessionClosedCallback,
    setAddToast,
    sessionStarted,
    inSession,
    remoteParticipantStatus,
    setAttached,
    showPermissionsModal,
    closePermissionsModal,
    isRecording,
    config: { shouldJoinOnOpen, shouldNotifyOnParticipantReady },
  } = useZoom()
  const hasFiredOpenEvent = useRef(false)
  const hasAttemptedJoin = useRef(false)
  const showInformedConsentBanner = !showRecordingConsentBanner && !!informedConsentBanner && sessionStarted
  const [informedConsentBannerHeight, setInformedConsentBannerHeight] = useState(0)
  useEffect(() => {
    if (!showInformedConsentBanner) {
      setInformedConsentBannerHeight(0)
      onInformedConsentBannerHeightChange?.(0)
    }
  }, [onInformedConsentBannerHeightChange, showInformedConsentBanner])
  const onBannerHeightChange = useCallback(
    (height: number) => {
      setInformedConsentBannerHeight(height)
      onInformedConsentBannerHeightChange?.(height)
    },
    [onInformedConsentBannerHeightChange],
  )
  const hasStarted = useRef(false)
  const startDate = useMemo(() => getDateFromAppointment(appointment), [appointment])

  const [theme, setTheme] = useState<Theme>(Theme.DARK)
  useEffect(() => {
    if (!shouldJoinOnOpen) {
      if (!hasFiredOpenEvent.current) {
        hasFiredOpenEvent.current = true
        onSessionOpened?.()
      }
      return
    }

    if (hasAttemptedJoin.current) {
      return
    }
    hasAttemptedJoin.current = true
    getSessionParams().then((sessionParams) => joinSession(sessionParams).then(() => onSessionOpened?.()))
  }, [getSessionParams, joinSession, onSessionOpened, shouldJoinOnOpen])

  useEffect(() => {
    setAttached(true)
    return () => setAttached(false)
  }, [setAttached])

  useEffect(() => {
    setSessionEndedCallback(onSessionEnded)
    setSessionClosedCallback(onSessionClosed)
    setAddToast(addToast)
  }, [addToast, onSessionClosed, onSessionEnded, setAddToast, setSessionClosedCallback, setSessionEndedCallback])

  useEffect(() => {
    if (sessionStarted && !hasStarted.current) {
      hasStarted.current = true
      onSessionStarted?.()
    }
  }, [onSessionStarted, sessionStarted])

  useEffect(() => {
    if (shouldNotifyOnParticipantReady && remoteParticipantStatus === RemoteParticipantStatus.READY) {
      new Audio(providerSessionAlertSound).play()
    }
  }, [remoteParticipantStatus, shouldNotifyOnParticipantReady])

  useEffect(() => {
    setTheme(!shouldJoinOnOpen && inSession && !sessionStarted ? Theme.LIGHT : Theme.DARK)
  }, [inSession, sessionStarted, shouldJoinOnOpen])
  return (
    <>
      <ThemeProvider theme={theme}>
        <Container
          fixed={displayMode === DisplayMode.FULL_SCREEN || sessionStarted}
          fullscreen={displayMode === DisplayMode.FULL_SCREEN}
        >
          <ZoomVideoBanner
            participantName={participantName}
            sessionStartDate={startDate}
            sessionDuration={appointment.appointmentDuration}
            participantFirstName={participantFirstName}
            isRecording={isRecording}
            avatarDetails={avatarDetails}
            sessionNumber={appointment.sessionNumber}
            hideSessionNumber={hideSessionNumber}
            hideNotRecordingBadge={hideNotRecordingBadge}
            messenger={messenger}
            onOpenMessagesPressed={onOpenMessagesPressed}
            unreadMessageCount={unreadMessageCount}
            onDisableRecordingPressed={onDisableRecordingPressed}
          />

          {showRecordingConsentBanner && sessionStarted && recordSessionProps && (
            <ThemeProvider>
              <RecordingConsentBanner {...recordSessionProps} />
            </ThemeProvider>
          )}
          {showInformedConsentBanner && informedConsentBanner && (
            <ThemeProvider>
              <InformedConsentSessionBanner
                status={informedConsentBanner.status}
                recipients={informedConsentBanner.recipients}
                onDismiss={informedConsentBanner.onDismiss}
                onHeightChange={onBannerHeightChange}
              />
            </ThemeProvider>
          )}
          <ZoomFullScreenView
            participantFirstName={participantFirstName}
            getSessionParams={getSessionParams}
            participantName={participantName}
            avatarDetails={avatarDetails}
            participantTitle={participantTitle}
            sessionStartDate={startDate}
            sessionDuration={appointment.appointmentDuration}
            sessionNumber={appointment.sessionNumber}
            hideSessionNumber={hideSessionNumber}
            hideRecordingWarning={hideRecordingWarning}
            showRecordingConsentBanner={showRecordingConsentBanner}
            informedConsentBannerHeight={showInformedConsentBanner ? informedConsentBannerHeight : 0}
          />
        </Container>
      </ThemeProvider>
      <ZoomPermissionsModal show={showPermissionsModal} onClose={closePermissionsModal} />
    </>
  )
}
